import React from 'react';
import { Link } from 'react-router-dom';
import { NgapAct } from '../../types/ngap';
import { generateActUrl } from '../../utils/urlUtils';
import RevalorizationSection from '../RevalorizationSection';
import ActHeader from './ActHeader';
import ActPricing from './ActPricing';
import ActDetails from './ActDetails';

interface ActCardProps {
  act: NgapAct;
  isInCart?: boolean;
  onToggleCartItem?: (act: NgapAct) => void;
  darkMode: boolean;
  showRemoveButton?: boolean;
  animationDelay?: number;
  displayCotationType?: 'current' | 'july2025' | 'july2026' | 'sept2026' | 'july2027';
  onHashtagClick?: (hashtag: string) => void;
  defaultRevaloExpanded?: boolean;
}

const ActCard: React.FC<ActCardProps> = ({
  act,
  isInCart = false,
  onToggleCartItem,
  darkMode,
  showRemoveButton = false,
  animationDelay = 0,
  displayCotationType,
  onHashtagClick,
  defaultRevaloExpanded = false
}) => {
  const actUrl = React.useMemo(() => generateActUrl(act), [act]);

  const activeCotationValue = React.useMemo(() => {
    if (displayCotationType && displayCotationType !== 'auto') {
      switch (displayCotationType) {
        case 'current':
          return act.cotation_valeur;
        case 'july2025':
          return act.cotation_valeur_july_2025 || act.cotation_valeur;
        case 'july2026':
          return act.cotation_valeur_july_2026 || act.cotation_valeur;
        case 'sept2026':
          return act.cotation_valeur_sept_2026 || act.cotation_valeur;
        case 'july2027':
          return act.cotation_valeur_july_2027 || act.cotation_valeur;
        default:
          return act.cotation_valeur;
      }
    }
    
    const now = new Date();
    if (act.revalo_july_2027 && act.cotation_valeur_july_2027 && now >= new Date('2027-07-01')) {
      return act.cotation_valeur_july_2027;
    }
    if (act.revalo_sept_2026 && act.cotation_valeur_sept_2026 && now >= new Date('2026-09-01')) {
      return act.cotation_valeur_sept_2026;
    }
    if (act.revalo_may_2026 && act.cotation_valeur_may_2026 && now >= new Date('2026-05-28')) {
      return act.cotation_valeur_may_2026;
    }
    if (act.revalo_july_2025 && act.cotation_valeur_july_2025 && now >= new Date('2025-07-01')) {
      return act.cotation_valeur_july_2025;
    }
    
    return act.cotation_valeur;
  }, [act, displayCotationType]);

  const isRevalorized = React.useMemo(() =>
    act.revalo_july_2025 || act.revalo_janv_2026 || act.revalo_may_2026 ||
    act.revalo_july_2026 || act.revalo_sept_2026 || act.revalo_july_2027,
    [act.revalo_july_2025, act.revalo_janv_2026, act.revalo_may_2026,
     act.revalo_july_2026, act.revalo_sept_2026, act.revalo_july_2027]
  );

  const animationStyle = React.useMemo(() => ({
    animationDelay: `${animationDelay}ms`,
    animation: 'fadeInUp 0.3s ease forwards'
  }), [animationDelay]);

  return (
    <div
      role="article"
      aria-labelledby={`act-title-${act.ID}`}
      aria-describedby={`act-details-${act.ID}`}
      className={`relative rounded-lg border p-4 transition-all duration-300 hover:shadow-md ${
        darkMode
          ? 'bg-appines-petrol border-appines-white/12 hover:border-appines-white/25'
          : 'bg-appines-white border-[rgba(1,42,54,0.12)] hover:border-appines-emerald/40'
      }`}
      style={animationStyle}
    >
      <ActHeader
        act={act}
        isInCart={isInCart}
        onToggleCartItem={onToggleCartItem}
        darkMode={darkMode}
        showRemoveButton={showRemoveButton}
        displayCotationType={displayCotationType}
      />

      <div className="mb-3">
        <Link
          to={actUrl}
          className={`block text-base font-semibold leading-snug hover:text-appines-emerald transition-colors ${
            darkMode ? 'text-appines-white' : 'text-appines-black'
          }`}
          id={`act-title-${act.ID}`}
          role="heading"
          aria-level="3"
        >
          {act.libelle}
        </Link>
      </div>

      <div id={`act-details-${act.ID}`} className="mb-3">
        <ActDetails
          act={act}
          darkMode={darkMode}
          onHashtagClick={onHashtagClick}
        />
      </div>

      <div className={`pt-3 border-t ${darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.08)]'}`}>
        <ActPricing
          act={act}
          darkMode={darkMode}
          activeCotationValue={activeCotationValue}
        />
      </div>

      {isRevalorized && (
        <RevalorizationSection act={act} darkMode={darkMode} defaultExpanded={defaultRevaloExpanded} />
      )}
    </div>
  );
};

export default React.memo(ActCard);
